import type { ExternalToast } from 'sonner'
import { toast as sonnerToast } from 'sonner'

const TOAST_CONSTANTS = {
  DEFAULT_DURATION: 4000,
  MIN_DURATION: 3000,
  MAX_DURATION: 10000,
  MS_PER_CHARACTER: 60,
} as const

/**
 * Calculate toast duration based on text length
 */
const getSmartToastDuration = (
  message: string | React.ReactNode,
  description?: ExternalToast['description'],
) => {
  const messageText = typeof message === 'string' ? message : ''
  const descriptionText = typeof description === 'string' ? description : ''
  const text = `${messageText} ${descriptionText}`.trim()

  if (text.length === 0) return TOAST_CONSTANTS.DEFAULT_DURATION

  // Aprox 60ms per character
  // Minimun of 3s for visibility, maximum of 10s
  return Math.min(
    TOAST_CONSTANTS.MAX_DURATION,
    Math.max(TOAST_CONSTANTS.MIN_DURATION, text.length * TOAST_CONSTANTS.MS_PER_CHARACTER),
  )
}

type ToastType = 'success' | 'error' | 'info' | 'warning'

const createToastProxy = (type?: ToastType) => {
  return (message: string | React.ReactNode, data?: ExternalToast) => {
    const duration = data?.duration || getSmartToastDuration(message, data?.description)
    const options: ExternalToast = { ...data, duration }

    if (type && type in sonnerToast) {
      const toastFn = sonnerToast[type] as (
        message: string | React.ReactNode,
        data?: ExternalToast,
      ) => string | number
      return toastFn(message, options)
    }
    return sonnerToast(message, options)
  }
}

export const toast = Object.assign(createToastProxy(), {
  success: createToastProxy('success'),
  error: createToastProxy('error'),
  info: createToastProxy('info'),
  warning: createToastProxy('warning'),
  message: createToastProxy(), // toast.message is just a default toast in sonner
  dismiss: sonnerToast.dismiss,
  loading: sonnerToast.loading,
  promise: sonnerToast.promise,
  custom: sonnerToast.custom,
})
